import React, { useEffect, useState } from 'react'
import { Button, Flex, H2, Input, Line, SelectDropdown, StatusLabel } from '../../lib/components'
import {
  SecondaryMarketOrder,
  SecondaryMarketSettings,
} from '../../services/secondary-market-service'
import './secondary-market.scss'

type SecondaryMarketOrdersPresenterProps = {
  orders: SecondaryMarketOrder[]
  settings: SecondaryMarketSettings
  loading: boolean
  statusFilter: string
  typeFilter: string
  matchModalOpen: boolean
  sellOrderId: number | null
  buyOrderId: number | null
  agreedPrice: string
  openSellOrders: SecondaryMarketOrder[]
  openBuyOrders: SecondaryMarketOrder[]
  onStatusFilterChange: (value: string) => void
  onTypeFilterChange: (value: string) => void
  onToggleWindow: () => void
  onSaveWindowLabel: (label: string) => void
  onOpenMatchModal: (prefill?: {
    sellOrderId?: number | null
    buyOrderId?: number | null
    agreedPrice?: string
  }) => void
  onCloseMatchModal: () => void
  onSellOrderIdChange: (id: number | null) => void
  onBuyOrderIdChange: (id: number | null) => void
  onAgreedPriceChange: (value: string) => void
  onMatch: () => void
  onStatusUpdate: (orderId: number, status: string) => void
}

const STATUS_FILTERS = [
  { text: 'All statuses', value: '' },
  { text: 'Open', value: 'OPEN' },
  { text: 'Matched', value: 'MATCHED' },
  { text: 'Settled', value: 'SETTLED' },
  { text: 'Cancelled', value: 'CANCELLED' },
  { text: 'Expired', value: 'EXPIRED' },
]

const TYPE_FILTERS = [
  { text: 'All types', value: '' },
  { text: 'Sell', value: 'SELL' },
  { text: 'Buy interest', value: 'BUY_INTEREST' },
]

const formatPrice = (value: number | null | undefined) =>
  value !== null && value !== undefined && Number.isFinite(Number(value))
    ? Number(value).toFixed(2)
    : '—'

const askOrOfferLabel = (order: SecondaryMarketOrder) => {
  if (order.type === 'BUY_INTEREST') {
    return order.limit_price !== null ? `Offer @ ${formatPrice(order.limit_price)}` : '—'
  }
  if (order.price_basis === 'LIMIT' && order.limit_price !== null) {
    return `Ask @ ${formatPrice(order.limit_price)}`
  }
  return order.reference_price !== null ? `At ref ${formatPrice(order.reference_price)}` : '—'
}

/** Seller's asking price: sell's limit, or the linked sell's ask for buy interest. */
const sellerAskPrice = (
  order: SecondaryMarketOrder,
  orderLookup: SecondaryMarketOrder[],
): number | null => {
  if (order.type === 'SELL') {
    return order.limit_price !== null && Number(order.limit_price) > 0
      ? Number(order.limit_price)
      : null
  }
  const targetSellId = Number(order.metadata?.target_sell_order_id)
  if (!Number.isFinite(targetSellId) || targetSellId <= 0) return null
  const sell = orderLookup.find((o) => o.id === targetSellId && o.type === 'SELL')
  if (!sell || sell.limit_price === null || Number(sell.limit_price) <= 0) return null
  return Number(sell.limit_price)
}

const suggestedAgreedPrice = (order: SecondaryMarketOrder) => {
  if (order.limit_price !== null && Number(order.limit_price) > 0) {
    return String(order.limit_price)
  }
  if (order.reference_price !== null && Number(order.reference_price) > 0) {
    return String(order.reference_price)
  }
  return ''
}

export const SecondaryMarketOrdersPresenter: React.FC<SecondaryMarketOrdersPresenterProps> = ({
  orders,
  settings,
  loading,
  statusFilter,
  typeFilter,
  matchModalOpen,
  sellOrderId,
  buyOrderId,
  agreedPrice,
  openSellOrders,
  openBuyOrders,
  onStatusFilterChange,
  onTypeFilterChange,
  onToggleWindow,
  onSaveWindowLabel,
  onOpenMatchModal,
  onCloseMatchModal,
  onSellOrderIdChange,
  onBuyOrderIdChange,
  onAgreedPriceChange,
  onMatch,
  onStatusUpdate,
}) => {
  const [windowLabelInput, setWindowLabelInput] = useState(settings.window_label ?? '')

  useEffect(() => {
    setWindowLabelInput(settings.window_label ?? '')
  }, [settings.window_label])

  const sellOptions = [
    { text: 'Select sell order', value: '' },
    ...openSellOrders.map((o) => ({
      text: `${o.order_id} — ${o.opportunity_title ?? o.opportunity_id} — qty ${
        o.quantity
      } — ${askOrOfferLabel(o)}`,
      value: String(o.id),
    })),
  ]

  const buysForSelectedSell = sellOrderId
    ? openBuyOrders.filter((o) => Number(o.metadata?.target_sell_order_id) === sellOrderId)
    : []

  const buyOptions = [
    {
      text: sellOrderId ? 'Select buy interest for this sell' : 'Select a sell order first',
      value: '',
    },
    ...buysForSelectedSell.map((o) => ({
      text: `${o.order_id} — ${o.opportunity_title ?? o.opportunity_id} — qty ${
        o.quantity
      } — ${askOrOfferLabel(o)}`,
      value: String(o.id),
    })),
  ]

  const openMatchForOrder = (order: SecondaryMarketOrder) => {
    if (order.type === 'SELL') {
      onOpenMatchModal({
        sellOrderId: order.id,
        buyOrderId: null,
        agreedPrice: suggestedAgreedPrice(order),
      })
      return
    }
    const targetSellId = Number(order.metadata?.target_sell_order_id)
    onOpenMatchModal({
      sellOrderId: Number.isFinite(targetSellId) ? targetSellId : null,
      buyOrderId: order.id,
      agreedPrice: suggestedAgreedPrice(order),
    })
  }

  return (
    <div className='secondary-market-admin'>
      <Flex variant='column'>
        <H2 text='Secondary Market Order Book' classes={['secondary-market-admin__title']} />
        <p className='secondary-market-admin__subtitle'>
          Manage the trading window, review the order book, and match sell/buy interest.
        </p>

        <section className='secondary-market-admin__toolbar'>
          <div className='secondary-market-admin__window-info'>
            <span className='secondary-market-admin__window-label'>Trading window</span>
            <span className='secondary-market-admin__window-value'>
              {settings.is_open ? 'Open' : 'Closed'}
              {settings.window_label ? ` · ${settings.window_label}` : ''}
            </span>
          </div>
          <div className='secondary-market-admin__window-actions'>
            <div className='secondary-market-admin__field'>
              <label htmlFor='sm-window-label'>Window label</label>
              <Input
                name='windowLabel'
                value={windowLabelInput}
                placeholder='e.g. 2026-Q3'
                handleChange={(e) => setWindowLabelInput(e.target.value)}
              />
            </div>
            <Button
              text='Save label'
              variant='secondary-btn'
              handleClick={() => onSaveWindowLabel(windowLabelInput)}
            />
            <Button
              text={settings.is_open ? 'Close window' : 'Open window'}
              variant={settings.is_open ? 'secondary-btn' : 'primary-btn'}
              handleClick={onToggleWindow}
            />
            <Button
              text='Match orders'
              variant='primary-btn'
              handleClick={() => onOpenMatchModal()}
            />
          </div>
        </section>

        <section className='secondary-market-admin__filters'>
          <SelectDropdown
            name='statusFilter'
            id='sm-status-filter'
            formId=''
            list={STATUS_FILTERS}
            value={statusFilter}
            placeholder='Filter by status'
            handleChange={(val) => onStatusFilterChange(val)}
          />
          <SelectDropdown
            name='typeFilter'
            id='sm-type-filter'
            formId=''
            list={TYPE_FILTERS}
            value={typeFilter}
            placeholder='Filter by type'
            handleChange={(val) => onTypeFilterChange(val)}
          />
        </section>

        <Line />

        {loading ? <p className='secondary-market-admin__empty'>Loading orders…</p> : null}

        {!loading && orders.length === 0 ? (
          <p className='secondary-market-admin__empty'>No secondary market orders found.</p>
        ) : null}

        {!loading && orders.length > 0 ? (
          <div className='w-100 table_scroll_horizontal'>
            <table className='styled-table'>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Type</th>
                  <th>Opportunity</th>
                  <th>Client</th>
                  <th>Qty</th>
                  <th>Ask / Offer</th>
                  <th>Seller ask</th>
                  <th>Status</th>
                  <th>Settlement</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.order_id}</td>
                    <td>{order.type === 'BUY_INTEREST' ? 'Buy interest' : 'Sell'}</td>
                    <td>{order.opportunity_title ?? order.opportunity_id}</td>
                    <td>{order.investor_name ?? order.investor_id}</td>
                    <td>{order.quantity}</td>
                    <td>{askOrOfferLabel(order)}</td>
                    <td>{formatPrice(sellerAskPrice(order, [...orders, ...openSellOrders]))}</td>
                    <td>
                      <StatusLabel type='info' text={order.status} variant='basic' />
                    </td>
                    <td>{order.settlement_status ?? '—'}</td>
                    <td>
                      <div className='secondary-market-admin__actions'>
                        {order.status === 'OPEN' ? (
                          <>
                            <Button
                              text='Match'
                              variant='primary-btn'
                              handleClick={() => openMatchForOrder(order)}
                            />
                            <Button
                              text='Expire'
                              variant='secondary-btn'
                              handleClick={() => onStatusUpdate(order.id, 'EXPIRED')}
                            />
                            <Button
                              text='Cancel'
                              variant='secondary-btn'
                              handleClick={() => onStatusUpdate(order.id, 'CANCELLED')}
                            />
                          </>
                        ) : null}
                        {order.status === 'MATCHED' ? (
                          <>
                            <Button
                              text='Settle'
                              variant='primary-btn'
                              handleClick={() => onStatusUpdate(order.id, 'SETTLED')}
                            />
                            <Button
                              text='Unmatch'
                              variant='secondary-btn'
                              handleClick={() => onStatusUpdate(order.id, 'OPEN')}
                            />
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Flex>

      {matchModalOpen ? (
        <div className='secondary-market-admin__modal-backdrop' role='presentation'>
          <div className='secondary-market-admin__modal' role='dialog' aria-modal='true'>
            <h2>Match sell and buy orders</h2>
            <SelectDropdown
              name='sellOrder'
              id='sm-match-sell'
              formId=''
              list={sellOptions}
              value={sellOrderId !== null ? String(sellOrderId) : ''}
              placeholder='Select sell order'
              handleChange={(val) => {
                const nextSellId = val ? Number(val) : null
                onSellOrderIdChange(nextSellId)
                if (
                  buyOrderId &&
                  !openBuyOrders.some(
                    (o) =>
                      o.id === buyOrderId &&
                      Number(o.metadata?.target_sell_order_id) === nextSellId,
                  )
                ) {
                  onBuyOrderIdChange(null)
                }
              }}
            />
            <SelectDropdown
              name='buyOrder'
              id='sm-match-buy'
              formId=''
              list={buyOptions}
              value={buyOrderId !== null ? String(buyOrderId) : ''}
              placeholder='Select buy interest'
              handleChange={(val) => onBuyOrderIdChange(val ? Number(val) : null)}
            />
            <div className='secondary-market-admin__field'>
              <label htmlFor='sm-agreed-price'>Agreed unit price</label>
              <Input
                name='agreedPrice'
                value={agreedPrice}
                placeholder='Enter agreed unit price'
                handleChange={(e) => onAgreedPriceChange(e.target.value)}
              />
            </div>
            <div className='secondary-market-admin__modal-actions'>
              <Button text='Close' variant='secondary-btn' handleClick={onCloseMatchModal} />
              <Button
                text='Match'
                variant='primary-btn'
                handleClick={onMatch}
                isDisabled={!sellOrderId || !buyOrderId || !agreedPrice}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
